import { useCallback, useEffect, useState } from "react";
import { IFilter, IFilterValue, IRequestValues, SortOrder } from "../../components/ui/Declarations";
import { useLocalStorage } from "../../components/ui/LocalStorage";

interface UsePagedListFiltersProps {
    filters: IFilter[];
    staticFilterValues: Record<string, any>;
    persistanceKey?: string;
}

export const usePagedListFilters = ({ filters, staticFilterValues, persistanceKey }: UsePagedListFiltersProps) => {
    const [filterValues, setFilterValues] = useState<IFilterValue[]>(
        filters.map((filter) => {
            return { filter: filter, value: filter.defaultValue ?? null } as IFilterValue;
        })
    );
    const [lastRequestValues, setLastRequestValues] = useState<IRequestValues | null>(null);

    const [storedFilters, setStoredFilters] = useLocalStorage<IFilterValue[]>(
        persistanceKey + "-filters",
        null
    );

    const filterChanged = useCallback((last: Record<string, any>, current: Record<string, any>): boolean => {
        for (const key in current) {
            if (last[key] !== current[key]) {
                return true;
            }
        }
        return false;
    }, []);

    const requestParamsChanged = useCallback((last: IRequestValues, current: IRequestValues): boolean => {
        return last.offset !== current.offset || 
               last.count !== current.count || 
               last.sortBy !== current.sortBy || 
               last.sortOrder !== current.sortOrder || 
               filterChanged(last.filter, current.filter);
    }, [filterChanged]);

    const handleFilterValueChanged = useCallback((filterValues: IFilterValue[]): void => {
        setFilterValues(filterValues);
        setStoredFilters(filterValues);
    }, [setStoredFilters]);

    const handleFilterReset = useCallback((): void => {
        const resetFilters = filters.map((filter) => {
            return { filter: filter, value: filter.defaultValue ?? null } as IFilterValue;
        });
        setFilterValues(resetFilters);
        setStoredFilters(resetFilters);
    }, [filters, setStoredFilters]);

    const buildRequestValues = useCallback((offset: number, count: number, sortBy: string, sortOrder: SortOrder): IRequestValues => {
        const filterData: Record<string, any> = {};

        // Add static filter values
        for (const key in staticFilterValues) {
            filterData[key] = staticFilterValues[key];
        }

        // Add dynamic filter values
        filterValues.forEach((item) => {
            filterData[item.filter.member] = item.value;
        });

        return {
            filter: filterData,
            offset: offset,
            count: count,
            sortBy: sortBy,
            sortOrder: sortOrder,
        };
    }, [filterValues, staticFilterValues]);

    // Initialize filters from stored values
    useEffect(() => {
        if (storedFilters) {
            setFilterValues(storedFilters);
        }
    }, [storedFilters]);

    return {
        filterValues,
        setFilterValues,
        lastRequestValues,
        setLastRequestValues,
        filterChanged,
        requestParamsChanged,
        handleFilterValueChanged,
        handleFilterReset,
        buildRequestValues
    };
};
